import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  Button,
  Alert,
  AlertIcon,
  Skeleton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Divider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVoiceService, VoiceRoom } from '../../hooks/useVoiceService';

const getRoomTypeLabel = (type: number) => {
  switch (type) {
    case 1: return 'Standard';
    case 2: return 'Workspace';
    case 3: return 'Event';
    case 4: return 'Gaming';
    default: return 'Unknown';
  }
};

export const VoiceRoomEditPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { getRoom, updateRoom } = useVoiceService();
  const toast = useToast();

  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 1,
    settings: {
      max_groups: 10,
      is_public: true,
      allow_guest_groups: false,
      auto_archive_empty_groups: false,
      auto_archive_days: 7,
      max_created_groups_per_user: 3,
      max_owned_groups_per_user: 2,
      default_group_settings: {
        max_members: 20,
        is_public: true,
        allow_guest: false,
        audio_quality: 2,
      },
    },
  });

  useEffect(() => {
    if (!roomId) return;
    
    loadRoom();
  }, [roomId]);

  const loadRoom = async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getRoom(roomId);
      const roomData = response.room;
      
      if (roomData) {
        setRoom(roomData);
        setFormData({
          name: roomData.name,
          description: roomData.description || '',
          type: roomData.type,
          settings: {
            max_groups: roomData.settings?.max_groups || 10,
            is_public: roomData.settings?.is_public ?? true,
            allow_guest_groups: roomData.settings?.allow_guest_groups ?? false,
            auto_archive_empty_groups: roomData.settings?.auto_archive_empty_groups ?? false,
            auto_archive_days: roomData.settings?.auto_archive_days || 7,
            max_created_groups_per_user: roomData.settings?.max_created_groups_per_user || 3,
            max_owned_groups_per_user: roomData.settings?.max_owned_groups_per_user || 2,
            default_group_settings: {
              max_members: roomData.settings?.default_group_settings?.max_members || 20,
              is_public: roomData.settings?.default_group_settings?.is_public ?? true,
              allow_guest: roomData.settings?.default_group_settings?.allow_guest ?? false,
              audio_quality: roomData.settings?.default_group_settings?.audio_quality || 2,
            },
          },
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!roomId || !formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setSaving(true);
      
      await updateRoom(roomId, {
        name: formData.name,
        description: formData.description,
        settings: formData.settings,
      });
      
      toast({
        title: 'Room updated successfully',
        status: 'success',
        duration: 3000,
      });
      
      // Navigate back to the room
      navigate(`/voice/rooms/${roomId}`);
    } catch (err: any) {
      toast({
        title: 'Failed to update room',
        description: err.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/voice/rooms/${roomId}`);
  };

  // Update left sidebar
  useEffect(() => {
    window.updateLeftSidebar?.([
      {
        label: 'All Rooms',
        path: '/voice/rooms',
      },
      {
        label: room?.name || 'Room',
        path: `/voice/rooms/${roomId}`,
      },
      {
        label: 'Edit Room',
        path: `/voice/rooms/${roomId}/edit`,
        active: true,
      },
    ]);

    return () => window.updateLeftSidebar?.([]);
  }, [roomId, room?.name]);

  if (!roomId) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Room ID is required
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <Skeleton height="40px" />
          <Skeleton height="200px" />
          <Skeleton height="100px" />
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!room) {
    return (
      <Box p={6}>
        <Alert status="warning">
          <AlertIcon />
          Room not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg">Edit Voice Room</Heading>
          <Text color="gray.500" mt={2}>
            Update room settings and configuration
          </Text>
        </Box>

        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Room Name</FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter room name..."
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter room description..."
              rows={3}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Room Type</FormLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: parseInt(e.target.value) }))}
            >
              <option value={1}>Standard</option>
              <option value={2}>Workspace</option>
              <option value={3}>Event</option>
              <option value={4}>Gaming</option>
            </Select>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Current: {getRoomTypeLabel(room.type)}
            </Text>
          </FormControl>

          <Divider />
          <Text fontWeight="medium">Room Settings</Text>

          <HStack>
            <FormControl>
              <FormLabel>Max Groups</FormLabel>
              <NumberInput
                value={formData.settings.max_groups}
                onChange={(_, value) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, max_groups: value || 10 }
                }))}
                min={1}
                max={50}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Auto Archive Days</FormLabel>
              <NumberInput
                value={formData.settings.auto_archive_days}
                onChange={(_, value) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, auto_archive_days: value || 7 }
                }))}
                min={1}
                max={365}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </HStack>

          <VStack align="stretch" spacing={3}>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="is-public" mb="0">
                Public Room
              </FormLabel>
              <Switch
                id="is-public"
                isChecked={formData.settings.is_public}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, is_public: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="allow-guest-groups" mb="0">
                Allow Guest Groups
              </FormLabel>
              <Switch
                id="allow-guest-groups"
                isChecked={formData.settings.allow_guest_groups}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, allow_guest_groups: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="auto-archive" mb="0">
                Auto Archive Empty Groups
              </FormLabel>
              <Switch
                id="auto-archive"
                isChecked={formData.settings.auto_archive_empty_groups}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, auto_archive_empty_groups: e.target.checked }
                }))}
              />
            </FormControl>
          </VStack>

          <Divider />
          <Text fontWeight="medium">Default Group Settings</Text>

          <HStack>
            <FormControl>
              <FormLabel>Max Members</FormLabel>
              <NumberInput
                value={formData.settings.default_group_settings.max_members}
                onChange={(_, value) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      max_members: value || 20
                    }
                  }
                }))}
                min={1}
                max={100}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Audio Quality</FormLabel>
              <Select
                value={formData.settings.default_group_settings.audio_quality}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      audio_quality: parseInt(e.target.value)
                    }
                  }
                }))}
              >
                <option value={1}>Low</option>
                <option value={2}>High</option>
                <option value={3}>Ultra</option>
              </Select>
            </FormControl>
          </HStack>

          <VStack align="stretch" spacing={3}>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="default-public" mb="0">
                Public by Default
              </FormLabel>
              <Switch
                id="default-public"
                isChecked={formData.settings.default_group_settings.is_public}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      is_public: e.target.checked
                    }
                  }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="default-allow-guest" mb="0">
                Allow Guests by Default
              </FormLabel>
              <Switch
                id="default-allow-guest"
                isChecked={formData.settings.default_group_settings.allow_guest}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      allow_guest: e.target.checked
                    }
                  }
                }))}
              />
            </FormControl>
          </VStack>
        </VStack>

        <HStack justify="end" spacing={4}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};